/**
 * Generate a URL-friendly slug from a name, treating **whitespace** as the only
 * word separator: other punctuation is dropped rather than turned into a break,
 * and hyphens already in the name survive.
 * E.g., "John Doe" -> "john-doe", "Meta (Facebook)" -> "meta-facebook",
 * but "O(n) Algorithms" -> "on-algorithms" (note: no break inside "O(n)").
 *
 * This is the slug written to the DB for admin-authored records — recipes,
 * subtopics, study plans, test templates, source content, system design
 * problems. Prefer {@link slugify} for anything matched against a slug
 * computed elsewhere (see its note on the SQL parity contract).
 */
export function generateSlugFromName(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single
    .replace(/^-|-$/g, '') // Remove leading/trailing hyphens
}

/**
 * Generate a URL-friendly slug from a name, treating **every** run of
 * non-alphanumeric characters as a word break.
 * E.g., "Meta (Facebook)" -> "meta-facebook", "O(n) Algorithms" ->
 * "o-n-algorithms".
 *
 * This is the semantic mirrored by the SQL expression in
 * `browseByTopic` (api/_trpc/routers/problems.ts); `lib/__tests__/
 * slug-parity.test.ts` guards the two against drifting. Use it for company,
 * tag, source, and problem slugs — anything a URL or a query may have to
 * re-derive from a display name.
 */
export function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
}
